import { lazy, Suspense } from "react";
import TopBanner from "@/components/TopBanner";
import HeroSection from "@/components/HeroSection";
import { useFunnelTracking } from "@/hooks/useFunnelTracking";
import TrackedSection from "@/components/TrackedSection";

// Lazy load below-fold components for faster Speed Index
const ModelShowcase = lazy(() => import("@/components/ModelShowcase"));
const SecondCTA = lazy(() => import("@/components/SecondCTA"));
const ShowcaseGallery = lazy(() => import("@/components/ShowcaseGallery"));
const WhyLoveBundle = lazy(() => import("@/components/WhyLoveBundle"));
const SocialProofGallery = lazy(() => import("@/components/SocialProofGallery"));
const BundleContents = lazy(() => import("@/components/BundleContents"));
const BusinessOpportunity = lazy(() => import("@/components/BusinessOpportunity"));
const CategoriesGrid = lazy(() => import("@/components/CategoriesGrid"));
const Compatibility = lazy(() => import("@/components/Compatibility"));
const WhyChoose = lazy(() => import("@/components/WhyChoose"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const Reviews = lazy(() => import("@/components/Reviews"));
const Guarantee = lazy(() => import("@/components/Guarantee"));
const FAQ = lazy(() => import("@/components/FAQ"));
const FinalCTA = lazy(() => import("@/components/FinalCTA"));

// Minimal loading placeholder for lazy components
const SectionLoader = () => (
  <div className="py-16 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const Index = () => {
  // Initialize funnel tracking (handles scroll, time on page, exit intent)
  useFunnelTracking();

  return (
    <div className="min-h-screen bg-background">
      <TopBanner />
      <main>
        {/* Critical above-fold content - loaded immediately */}
        <TrackedSection sectionId="hero" eventName="FunnelSectionHero">
          <HeroSection />
        </TrackedSection>
        
        {/* Below-fold content - lazy loaded for better Speed Index */}
        <Suspense fallback={<SectionLoader />}>
          <div className="content-below-fold">
            <TrackedSection sectionId="model-showcase" eventName="FunnelSectionModelShowcase">
              <ModelShowcase />
            </TrackedSection>
            
            <TrackedSection sectionId="second-cta" eventName="FunnelSectionSecondCTA">
              <SecondCTA />
            </TrackedSection>
            
            <TrackedSection sectionId="gallery" eventName="FunnelSectionGallery">
              <ShowcaseGallery />
            </TrackedSection>
            
            <TrackedSection sectionId="why-love" eventName="FunnelSectionWhyLove">
              <WhyLoveBundle />
            </TrackedSection>
            
            <TrackedSection sectionId="social-proof" eventName="FunnelSectionSocialProof">
              <SocialProofGallery />
            </TrackedSection>
            
            <TrackedSection sectionId="bundle-contents" eventName="FunnelSectionBundleContents">
              <BundleContents />
            </TrackedSection>
            
            <TrackedSection sectionId="business" eventName="FunnelSectionBusiness">
              <BusinessOpportunity />
            </TrackedSection>
            
            <TrackedSection sectionId="categories" eventName="FunnelSectionCategories">
              <CategoriesGrid />
            </TrackedSection>
            
            <TrackedSection sectionId="compatibility" eventName="FunnelSectionCompatibility">
              <Compatibility />
            </TrackedSection>
            
            <TrackedSection sectionId="why-choose" eventName="FunnelSectionWhyChoose">
              <WhyChoose />
            </TrackedSection>
            
            <TrackedSection sectionId="testimonials" eventName="FunnelSectionTestimonials">
              <Testimonials />
            </TrackedSection>
            
            <TrackedSection sectionId="reviews" eventName="FunnelSectionReviews">
              <Reviews />
            </TrackedSection>
            
            <TrackedSection sectionId="guarantee" eventName="FunnelSectionGuarantee">
              <Guarantee />
            </TrackedSection>
            
            <TrackedSection sectionId="faq" eventName="FunnelSectionFAQ">
              <FAQ />
            </TrackedSection>
            
            <TrackedSection sectionId="final-cta" eventName="FunnelSectionFinalCTA">
              <FinalCTA />
            </TrackedSection>
          </div>
        </Suspense>
      </main>
      
      <footer className="bg-muted/50 border-t border-border py-6 px-4">
        <div className="container mx-auto space-y-4">
          <div className="flex justify-center gap-6">
            <a href="/privacy-policy" className="text-xs text-muted-foreground hover:text-primary transition-colors">
              Privacy Policy
            </a>
            <a href="/terms-of-use" className="text-xs text-muted-foreground hover:text-primary transition-colors">
              Terms of Use
            </a>
          </div>
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            This site is not a part of the Facebook website or Facebook Inc. Additionally, this site is NOT endorsed by Facebook in any way. FACEBOOK is a trademark of FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
